import { formatDate } from '@angular/common';
import {
  Component,
  DestroyRef,
  Input,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { BehaviorSubject, filter, switchMap, take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { SpecialCharacters } from 'src/app/core/enums';
import { Screens } from 'src/app/core/enums/role-permissions.enum';
import { CurrentLocation } from 'src/app/core/models/admin/location-admin.model';
import { UserAdmin } from 'src/app/core/models/admin/user-admin.model';
import { Items } from 'src/app/core/models/common/items.model';
import { TableColumn } from 'src/app/core/models/common/table-column.model';
import { FileUploadResults } from 'src/app/core/models/file-upload-results.model';
import { RevenueRecoveryRequest } from 'src/app/core/models/revenue-recovery/revenue-recovery';
import { LocationService } from 'src/app/core/services/admin/location/location.service';
import { UserService } from 'src/app/core/services/admin/user/user.service';
import {
  FileParseType,
  FileReturn,
} from '../../file-upload/file-upload.constants';
import { Intercept } from '../../intercept';
import {
  IMAGE_SRC,
  NO_RESULTS,
  VALIDATION_MESSAGE,
} from '../../shared-constants';
import { PtnRangePackageValidator } from '../../validators';
import {
  CONST,
  ColumnIds,
  ColumnText,
  InboundScan,
  SORT_OPTIONS,
  SORT_OPTION_LIST,
} from './handling-scan-table.constant';

@Component({
  selector: 'app-handling-scan-table',
  templateUrl: './handling-scan-table.component.html',
  styleUrls: ['./handling-scan-table.component.scss'],
  standalone: false,
})
export class HandlingScanTableComponent extends Intercept implements OnInit {
  readonly destroy = inject(DestroyRef);
  private userService = inject(UserService);
  private locationService = inject(LocationService);
  private readonly fb = inject(FormBuilder);

  //Header text for screen
  @Input() header!: string;
  //Type of screen, used to call correct apis
  @Input() screen!: Screens;

  readonly tableData = signal<InboundScan[]>([]);
  readonly SRC = IMAGE_SRC;

  readonly displayedColumns = Object.values(ColumnIds);
  readonly COLUMNTEXT = ColumnText;
  readonly NO_RESULTS = NO_RESULTS;
  readonly sortOption = SORT_OPTIONS;
  readonly SORT_OPTION_LIST = SORT_OPTION_LIST;
  readonly PARSE_TYPE = FileParseType;

  readonly currentLocation = signal<CurrentLocation | undefined>(undefined);
  readonly currentUser = signal<UserAdmin | undefined>(undefined);

  readonly CONST = { ...CONST, ...VALIDATION_MESSAGE };
  readonly SpecialCharacters = SpecialCharacters;

  dataSource = new MatTableDataSource<InboundScan>();

  form!: FormGroup;
  dropdownOpen = [false];
  minSortDate = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    new Date().getDate() - 1,
  );
  maxSortDate = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    new Date().getDate() + 3,
  );

  // Create column search objects
  createTableColumn(columnId: string): TableColumn {
    return {
      itemId: columnId,
      ids: [],
      formControl: new FormControl(''),
    };
  }

  // Create admin table columns
  trackingNumberData = this.createTableColumn(ColumnIds.TRACKING_NUMBER);
  sortDate = this.createTableColumn(ColumnIds.SORT_DATE);
  sortCode = this.createTableColumn(ColumnIds.SORT_CODE);

  resetSelectDropdowns = new BehaviorSubject<boolean>(false);
  sortCodeOption: Items[] = [];
  isFormSubmitted = signal(false);
  isFileUpload = false;
  fileUploadResults = signal<FileUploadResults | null>(null);
  tableMinSortDate = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    new Date().getDate() - 1,
  );
  tableMaxSortDate = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    new Date().getDate() + 3,
  );
  enabledDates: Date[] = [];
  /**
   * OnInit Lifecycle hook
   *
   * @memberof InboundScanTableComponent
   */
  ngOnInit(): void {
    this.form = this.fb.group({
      sortDate: [new Date(), Validators.required],
      sortCode: [null, Validators.required],
      trackingNumbers: [
        '',
        Validators.compose([
          Validators.required,
          PtnRangePackageValidator.trackingNumberValidator(),
        ]),
      ],
    });

    this.dataSource.filterPredicate = this.createFilter();
    this.getCurrentUser();
    this.setSortCodeOption();
    this.getCurrentLocation();
  }

  /**
   * get current user
   *
   * @memberof HandlingScanTableComponent
   */
  getCurrentUser() {
    this.userService
      .getCurrentUser$()
      .pipe(
        filter((user) => !!user && !!Object.keys(user).length),
        takeUntilDestroyed(this.destroy),
        switchMap((_user) => {
          this.currentUser.set(_user);
          return this.getIntercepts();
        }),
      )
      .subscribe();
  }

  getCurrentLocation() {
    this.locationService
      .getCurrentLocation$()
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((val) => {
        this.currentLocation.set(val);
        this.resetState();
      });
  }

  resetState() {
    this.tableData.set([]);
    this.dataSource.data = [];
    this.form.reset({
      sortDate: new Date(),
    });
  }

  setSortCodeOption() {
    this.sortCodeOption = [];
    this.dataSource.data.forEach((item) => {
      const isSortCodeAdded = this.sortCodeOption.find(
        (i) => i.id === item.sortCode,
      );
      if (!isSortCodeAdded) {
        this.sortCodeOption.push({
          id: item.sortCode,
          selected: true,
        });
      }
    });
  }

  setDateRangeValidation() {
    const dates = this.dataSource.data.map((item) =>
      new Date(item.sortDate).getTime(),
    );
    // Find min date
    this.tableMinSortDate = new Date(Math.min(...dates));

    // Find max date
    this.tableMaxSortDate = new Date(Math.max(...dates));

    // enabled dates
    this.enabledDates = dates.map((d) => new Date(d));
  }

  /**
   *
   * Create filter for table
   * @private
   * @return {*}  {(data: InboundScan, filter: string) => boolean}
   * @memberof InboundScanTableComponent
   */
  private createFilter(): (data: InboundScan, filter: string) => boolean {
    return (data: InboundScan, filter: string) => {
      const filters = JSON.parse(filter);

      return Object.keys(filters).every((filterKey) => {
        //Allows opening of row if filters are applied already
        if (filters[filterKey] === '') {
          return true;
        }
        return this.filterByColumn(filterKey, data, filters);
      });
    };
  }

  /**
   * Filter column by column id
   *
   * @private
   * @param {string} filterKey
   * @param {*} data
   * @param {*} filters
   * @return {*}
   * @memberof InboundScanTableComponent
   */
  private filterByColumn(
    filterKey: string,
    data: InboundScan,
    filters: InboundScan,
  ) {
    switch (filterKey) {
      case ColumnIds.SORT_DATE:
        if (filters.sortDate !== '') {
          const filteredStartDate = new Date(filters.sortDate);
          const dataDate = new Date(data.sortDate);
          if (dataDate.getTime() === filteredStartDate.getTime()) {
            return true;
          }
        }
        return false;
      case ColumnIds.SORT_CODE: {
        const value = filters[filterKey];
        const rowValue = data[filterKey];
        const searchData = rowValue?.toString().toLowerCase() || '';
        const searchValue = value?.toString().toLowerCase();
        if (searchValue) {
          return searchValue.includes(searchData);
        }
        return searchData.includes(searchValue);
      }
      default:
        return this.setDefaults(filters, data, filterKey);
    }
  }

  /**
   * return default values
   *
   * @private
   * @param {string} filterKey
   * @param {*} data
   * @param {*} filters
   * @return {*}
   * @memberof OutboundScanTableComponent
   */
  private setDefaults(
    filters: InboundScan,
    data: InboundScan,
    filterKey: string,
  ) {
    const value = filters[filterKey as keyof InboundScan];
    const rowValue = data[filterKey as keyof InboundScan];
    const searchData = rowValue?.toString().toLowerCase() || '';
    const searchValue = value?.toString().toLowerCase();
    return searchData.includes(searchValue);
  }

  /**
   * Triggers when a dropdown option is checked/unchecked
   *
   * @param {*} $event
   * @param {AdminTableColumn} field
   * @memberof InboundScanTableComponent
   */
  onStatusChangeForDropdowns($event: string | string[], field: TableColumn) {
    field.ids[0] = $event as unknown as Items;
    if (field.itemId === ColumnIds.SORT_CODE) {
      this.sortCode.formControl.setValue($event);
    }
    this.search();
  }

  /**
   * Filtering through the search bar
   *
   * @memberof InboundScanTableComponent
   */
  search() {
    this.tableData();
    this.commonUtils.searchFilter(
      [this.trackingNumberData, this.sortDate],
      this.dataSource,
      [this.sortCode],
    );
  }

  /**
   * Clear all the filters if user has searched/filtered any column
   *
   * @memberof InboundScanTableComponent
   */
  clearAllFilters() {
    this.commonUtils.clearAllFilters(
      this.dataSource,
      this.tableData(),
      [this.trackingNumberData.formControl, this.sortDate.formControl],
      [this.sortCode],
    );
    this.resetSelectDropdowns.next(true);
  }

  /**
   * Figure out if filter button is enabled
   * Should not be enabled if user has not filtered any column
   *
   * @readonly
   * @memberof InboundScanTableComponent
   */
  get isFilterEnabled() {
    return (
      this.trackingNumberData.formControl.value !== '' ||
      this.sortDate.formControl.value !== '' ||
      this.sortCode.ids.length > 0
    );
  }

  submit(fileData: FileReturn, isFileUpload: boolean) {
    this.isFileUpload = isFileUpload;

    const trackingNumber = fileData.returnData
      .flatMap((val) => val)
      .map((val) => val.trackingNumber);

    const intercept = this.intercepts().filter((item) =>
      trackingNumber.includes(item.trackingNumber),
    );

    if (intercept.length > 0) {
      this.commonUtils
        .openPackageInterceptPopup(
          intercept.map((val) => val.trackingNumber).toString(),
          !!intercept.find((val) => val.interceptType === 'LOC'),
        )
        .afterClosed()
        .subscribe((resp) => {
          if (resp === 'yes') {
            this.upload(fileData);
          } else {
            this.form.reset({
              sortDate: this.commonUtils.getDateFromString(
                this.form.get('sortDate')?.value,
              ),
              sortCode: this.form.get('sortCode')?.value,
            });
          }
        });
    } else {
      this.upload(fileData);
    }
  }

  private upload(fileData: FileReturn) {
    if (this.isFileUpload) {
      this.fileUpload(fileData);
    } else {
      this.manualUpload(fileData);
    }
  }

  private manualUpload(fileData: FileReturn) {
    this.commonUtils.updateLoadingModal(true);

    this.isFormSubmitted.set(true);

    const trackingNumber = fileData.returnData
      .flatMap((val) => val)
      .map((val) => val.trackingNumber);

    const intercept = this.intercepts().find(
      (item) => item.trackingNumber === trackingNumber[0],
    );

    const isLocalIntercept = intercept?.interceptType === 'LOC';

    const revenueRecoveryRequest: RevenueRecoveryRequest = {
      sortDate: formatDate(
        this.form.get('sortDate')?.value,
        'yyyy-MM-dd',
        'en-US',
      ),
      trackingNumber: trackingNumber[0],
      sortCode: this.form.get('sortCode')?.value,
      intercept: !!(intercept && !isLocalIntercept),
      localIntercept: isLocalIntercept,
      sizeAudit: null,
      weightAudit: null,
      wWEF: null,
    };

    this._revenueRecoveryService
      .getRevenueRecovery(revenueRecoveryRequest, 4)
      .pipe(take(1))
      .subscribe((val) => {
        if (val.code === 200) {
          this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);

          const index = this.tableData().findIndex(
            (i) => i.trackingNumber === trackingNumber[0],
          );

          const formData = {
            sortDate: this.form.get('sortDate')?.value,
            sortCode: this.form.get('sortCode')?.value,
            trackingNumber: trackingNumber[0],
          };

          if (index > -1) {
            this.tableData.update((current) => {
              current[index] = formData;

              return [...current];
            });
          } else {
            this.tableData.update((current) => {
              return [{ ...formData }, ...current];
            });
          }
          this.dataSource.data = this.tableData();
          this.setSortCodeOption();
          this.setDateRangeValidation();
          this.form.reset({
            sortDate: this.commonUtils.getDateFromString(
              revenueRecoveryRequest.sortDate,
            ),
          });
        }
      });
  }

  private fileUpload(fileData: FileReturn) {
    this.commonUtils.updateLoadingModal(true);

    const apis$ = fileData.returnData.map((val) => {
      const revenueRecoveryRequest = {
        sortCode: this.form.value.sortCode,
        additionalHandling: val.map((val) => val.trackingNumber),
        sortDate: formatDate(this.form.value.sortDate, 'yyyy-MM-dd', 'en-US'),
        intercept: false,
        localIntercept: false,
        sizeAudit: null,
        weightAudit: null,
        wWEF: null,
      };

      return {
        numbers: val.map((val) => val.trackingNumber),
        call$: this._revenueRecoveryService
          .revenueRecoveryFileUpload(revenueRecoveryRequest, 4)
          .pipe(take(1)),
      };
    });

    this.commonUtils
      .runFileUploadCallsInChunks(
        apis$,
        this.commonUtils.generateUploadString(
          new Date(),
          this.currentLocation()?.location?.locationDetails.code || '',
          this.currentUser()?.loginId || '',
        ),
        fileData.fileName,
        fileData.fileSize,
      )
      .subscribe((succeeded) => {
        if (succeeded) {
          this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
        }
      });
  }
}
